import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Smartphone, Laptop, Gamepad2, Tablet, ShieldCheck, Clock, Award, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
    const [featuredProducts, setFeaturedProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const response = await axios.get(`${API}/api/products`);
                setFeaturedProducts(response.data.slice(0, 4));
            } catch {
                setFeaturedProducts([]);
            } finally {
                setLoading(false);
            }
        };
        fetchProducts();
    }, []);

    const services = [
        { icon: Smartphone, title: 'Smartphone', desc: 'Riparazione schermi, batterie, connettori' },
        { icon: Tablet, title: 'Tablet', desc: 'Display, batterie, porte di ricarica' },
        { icon: Laptop, title: 'Computer', desc: 'Upgrade SSD, pulizia, riparazione' },
        { icon: Gamepad2, title: 'Console', desc: 'PS5, Xbox, Nintendo Switch' },
    ];

    const features = [
        { icon: ShieldCheck, title: 'Garanzia 6 Mesi', desc: 'Su tutte le riparazioni' },
        { icon: Clock, title: 'Riparazione Express', desc: 'In giornata per la maggior parte' },
        { icon: Award, title: 'Tecnici Certificati', desc: 'Personale qualificato' },
    ];

    return (
        <div className="min-h-screen">
            {/* Hero Section */}
            <section className="relative overflow-hidden py-20 lg:py-32">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-secondary/10" />
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div className="animate-fade-in">
                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                                <span className="text-primary">Ripariamo</span> i tuoi dispositivi
                                <br />
                                <span className="text-white">con cura</span>
                            </h1>
                            <p className="text-lg text-muted mb-8 max-w-lg">
                                Centro assistenza professionale per smartphone, tablet, computer e console gaming. 
                                Acquista dispositivi ricondizionati con garanzia.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Link to="/prenota" data-testid="hero-book-btn">
                                    <Button className="btn-primary text-lg px-8 py-4">
                                        Prenota Riparazione
                                        <ArrowRight className="ml-2 w-5 h-5" />
                                    </Button>
                                </Link>
                                <Link to="/store" data-testid="hero-store-btn">
                                    <Button variant="outline" className="border-border hover:bg-surface-elevated text-lg px-8 py-4">
                                        Esplora Store
                                    </Button>
                                </Link>
                            </div>
                        </div>
                        <div className="relative hidden lg:block">
                            <div className="relative rounded-2xl overflow-hidden border border-border glow-border">
                                <img 
                                    src="https://images.unsplash.com/photo-1560165143-fa7e2d9e594c?w=600" 
                                    alt="Riparazione dispositivi"
                                    className="w-full h-[400px] object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="py-20 bg-surface/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl sm:text-4xl font-bold mb-4">I Nostri Servizi</h2>
                        <p className="text-muted max-w-2xl mx-auto">
                            Offriamo riparazioni professionali per tutti i tuoi dispositivi elettronici
                        </p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {services.map((service, index) => (
                            <Link 
                                key={service.title} 
                                to="/riparazioni" 
                                className="service-card group"
                                data-testid={`service-card-${index}`}
                            >
                                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                                    <service.icon className="w-6 h-6 text-primary" />
                                </div>
                                <h3 className="font-semibold text-lg mb-2">{service.title}</h3>
                                <p className="text-muted text-sm">{service.desc}</p>
                                <ChevronRight className="w-5 h-5 text-primary mt-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features */}
            <section className="py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid sm:grid-cols-3 gap-8">
                        {features.map((feature) => (
                            <div key={feature.title} className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                    <feature.icon className="w-6 h-6 text-primary" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-lg mb-1">{feature.title}</h3>
                                    <p className="text-muted text-sm">{feature.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Featured Products */}
            <section className="py-20 bg-surface/30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center mb-12">
                        <div>
                            <h2 className="text-3xl sm:text-4xl font-bold mb-2">Prodotti in Evidenza</h2>
                            <p className="text-muted">Dispositivi ricondizionati e accessori</p>
                        </div>
                        <Link to="/store" className="hidden sm:flex items-center gap-2 text-primary hover:underline" data-testid="view-all-products">
                            Vedi tutti
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    
                    {loading ? (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {[1, 2, 3, 4].map((i) => (
                                <div key={i} className="bg-surface rounded-xl h-80 animate-pulse" />
                            ))}
                        </div>
                    ) : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {featuredProducts.map((product) => (
                                <Link 
                                    key={product.id} 
                                    to={`/store/${product.id}`} 
                                    className="product-card"
                                    data-testid={`product-card-${product.id}`}
                                >
                                    <div className="aspect-square bg-surface-elevated">
                                        <img 
                                            src={product.image_url} 
                                            alt={product.name}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="p-4">
                                        <span className="text-xs text-primary font-medium uppercase tracking-wider">
                                            {product.category === 'new' ? 'Nuovo' :
                                             product.category === 'refurbished' ? 'Ricondizionato' : 
                                             product.category === 'accessory' ? 'Accessorio' : 'Ricambio'}
                                        </span>
                                        <h3 className="font-semibold mt-1 line-clamp-2">{product.name}</h3>
                                        <p className="text-2xl font-bold text-primary mt-2">
                                            €{product.price.toFixed(2)}
                                        </p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                    
                    <div className="mt-8 text-center sm:hidden">
                        <Link to="/store">
                            <Button variant="outline" className="border-border">
                                Vedi tutti i prodotti
                                <ArrowRight className="ml-2 w-4 h-4" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="bg-gradient-to-r from-primary/20 to-secondary/20 rounded-2xl p-8 lg:p-12 border border-primary/30 text-center">
                        <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                            Hai bisogno di una riparazione?
                        </h2>
                        <p className="text-muted mb-8 max-w-2xl mx-auto">
                            Prenota ora il tuo appuntamento e porta il tuo dispositivo in negozio. 
                            Riceverai una diagnosi gratuita.
                        </p>
                        <Link to="/prenota" data-testid="cta-book-btn">
                            <Button className="btn-primary text-lg px-8 py-4">
                                Prenota Appuntamento
                                <ArrowRight className="ml-2 w-5 h-5" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default HomePage;
